<script lang="ts">
const emptyValue = '-'
</script>
<script setup lang="ts">
import {
 EntityCustomMetadataDto,
  type EntityCustomMetadataEntryUserDto,
  type EntityCustomMetadataEntryLabelDto,
  type EntityCustomMetadataEntryStringDto,
  type EntityCustomMetadataEntryTeamDto,
} from '@/api';
import SChip from '@/components/SChip.vue';
import SAvatars from '@/components/SAvatars/SAvatars.vue';
import { getProviderIconSrc } from '@/utils/provider-icons';

interface CustomMetadataDisplayProps {
  customMetadata: Array<EntityCustomMetadataDto>;
  isSmall?: boolean;
  hasNarrowLabel?: boolean;
}
defineProps({
  customMetadata: { default: () => [] },
  isSmall: { type: Boolean, default: false },
  hasNarrowLabel: { type: Boolean, default: false }
});



/**
 * Convert custom metadata user entry to component expected format
 * @param user
 */
const mapUserEntry = (user: EntityCustomMetadataEntryUserDto) => ({
  id: user.userId,
  name: user.name,
  email: user.login,
});

/**
 * Make sure user entries are properly typed before mapping
 * @param entries
 */
const mapUserEntries = (
  entries: (EntityCustomMetadataEntryUserDto | EntityCustomMetadataEntryLabelDto | EntityCustomMetadataEntryStringDto | EntityCustomMetadataEntryTeamDto)[],
) => entries.map((user) => mapUserEntry(user as EntityCustomMetadataEntryUserDto));

/**
 * Convert custom metadata team entry to component expected format
 * @param team
 */
const mapTeamEntry = (team: EntityCustomMetadataEntryTeamDto) => ({
  id: team.teamId,
  name: team.name,
  type: 'team',
});

/**
 * Make sure team entries are properly typed before mapping
 * @param entries
 */
const mapTeamEntries = (
  entries: (EntityCustomMetadataEntryLabelDto | EntityCustomMetadataEntryStringDto | EntityCustomMetadataEntryUserDto | EntityCustomMetadataEntryTeamDto)[],
) => entries.map((team) => mapTeamEntry(team as EntityCustomMetadataEntryTeamDto));

/**
 * Convert custom metadata entries to a displayable string
 * @param entries
 */
const entriesToDisplayableString = (
  entries: (EntityCustomMetadataEntryLabelDto | EntityCustomMetadataEntryStringDto | EntityCustomMetadataEntryUserDto | EntityCustomMetadataEntryTeamDto)[],
  type: EntityCustomMetadataDto.type,
) => {
  if (entries.length > 0) {
    if (type === EntityCustomMetadataDto.type.STRING || type === EntityCustomMetadataDto.type.LABEL) {
      return entries.map((entry) => (entry as EntityCustomMetadataEntryStringDto).value).join(', ');
    }
  }
  return emptyValue;
};

/**
 * Properly type entries as EntityCustomMetadataEntryLabelDto, for TS validation
 * @param entries
 */
const entriesAsEntityCustomMetadataEntryLabelDto = (
  entries: (EntityCustomMetadataEntryLabelDto | EntityCustomMetadataEntryStringDto | EntityCustomMetadataEntryUserDto | EntityCustomMetadataEntryTeamDto)[],
) => entries as EntityCustomMetadataEntryLabelDto[];

const isExternalCatalogMetadata = (customMetadata: EntityCustomMetadataDto) => customMetadata.lineagePlatform != null && customMetadata.lineagePlatform !== EntityCustomMetadataDto.lineagePlatform.SIFFLET;

</script>
<template lang="pug">
div.custom-metadata-display
  .mb-5( v-for="data in customMetadata" :key="data.id" )
    SLabel( :as-columns="!isSmall" :label-max-width="hasNarrowLabel ? '300px' : ''" )
      span {{ data.name }}
      v-tooltip( top max-width="300" v-if="data.description")
        template(v-slot:activator='{ on }')
          span(v-on="on")
            SIcon.ml-2( icon="icon-question-circle-outline" v-on="on" size="small" color="iconNeutral")
        span {{ data.description }}
        div.pt-4.tooltip-arrow-bottom

      .pt-2( slot="input" )
        template(v-if="data.type === EntityCustomMetadataDto.type.STRING")
          .d-flex.align-center
            SIcon.mr-1( v-if="isExternalCatalogMetadata(data)" color="iconNeutral" :icon="getProviderIconSrc(`${data.lineagePlatform}_LOCK`)" :alt="data.lineagePlatform" size="small" )
            span {{ entriesToDisplayableString(data.entries, EntityCustomMetadataDto.type.STRING) }}

        template(v-else-if="data.type === EntityCustomMetadataDto.type.LABEL")
          template( v-if="data.entries.length > 0" )
            SChip.mr-2.mb-2(
              v-for="item in entriesAsEntityCustomMetadataEntryLabelDto(data.entries)"
              :key="item.id"
              :text="item.value"
              :icon="isExternalCatalogMetadata(data) ? getProviderIconSrc(`${data.lineagePlatform}_LOCK`) : undefined"
              data-track="custom-metadata-label-selector-item"
              plain
            )
          span( v-else ) {{ emptyValue }}

        template(v-else-if="data.type === EntityCustomMetadataDto.type.USER")
          span( v-if="data.entries.length > 0" )
            SAvatars(
              :users="mapUserEntries(data.entries)"
              wrapped
            )
          span( v-else ) {{ emptyValue }}

        template(v-else-if="data.type === EntityCustomMetadataDto.type.TEAM")
          span( v-if="data.entries.length > 0" )
            SAvatars(
              :users="mapTeamEntries(data.entries)"
              :size="20"
              wrapped
            )
          span( v-else ) {{ emptyValue }}
</template>
